// src/components/SanitizationModal.js
// PHASE 3: Add Save Functionality to Firebase Storage

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    TextField
} from '@mui/material';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const SanitizationModal = ({ 
    open, 
    onClose, 
    pathOrUrl, 
    docType = 'document' 
}) => {
    // Phase 2 state (preserved)
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Phase 3 state additions
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalContent, setOriginalContent] = useState('');

    // Load content when modal opens
    useEffect(() => {
        if (open && pathOrUrl) {
            loadContent();
        }
    }, [open, pathOrUrl]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setContent('');
            setOriginalContent('');
            setError(null);
            setLoading(false);
            setSaving(false);
            setHasChanges(false);
        }
    }, [open]);

    // Track changes to content
    useEffect(() => {
        setHasChanges(content !== originalContent && originalContent !== '');
    }, [content, originalContent]);

    const loadContent = async () => {
        setLoading(true);
        setError(null);
        
        try {
    console.log('🔍 Starting content load for path:', pathOrUrl);

    // STEP 1: Get a fresh, valid signed URL from the backend.
    // (We will create this 'getFileDownloadUrl' function in the next step).
    const getFileDownloadUrl = httpsCallable(functions, 'getFileDownloadUrl');
    const urlResult = await getFileDownloadUrl({ storagePath: pathOrUrl });

    if (!urlResult.data || !urlResult.data.downloadUrl) {
        throw new Error("Backend function did not return a valid download URL.");
    }

    const validDownloadUrl = urlResult.data.downloadUrl;
    console.log('✅ Successfully got new signed URL.');

    // STEP 2: Fetch the content using the new URL from the browser
    const response = await fetch(validDownloadUrl);
    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to fetch content. Status: ${response.status}. Message: ${errorBody}`);
    }
    const textContent = await response.text();

    console.log('✅ Content loaded successfully');
    const loadedContent = textContent || 'No content found';
    setContent(loadedContent);
    setOriginalContent(loadedContent); // Also update original content for change tracking

} catch (error) {
    console.error('❌ Error loading content:', error);
    setError(`Failed to load content: ${error.message}`);
} finally {
    setLoading(false);
}
    };

    const saveContent = async () => {
        if (!hasChanges) {
            toast.info('No changes to save');
            return;
        }

        setSaving(true);
        
        try {
            console.log('💾 Saving content to:', pathOrUrl);
            
            // We'll create this function next
            const saveStorageFileContent = httpsCallable(functions, 'saveStorageFileContent');
            const result = await saveStorageFileContent({
                storagePath: pathOrUrl,
                content: content
            });
            
            console.log('✅ Content saved successfully');
            setOriginalContent(content); // Update original to match current
            setHasChanges(false);
            toast.success('Content saved successfully!');
            
        } catch (error) {
            console.error('❌ Error saving content:', error);
            toast.error(`Failed to save content: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (hasChanges) {
            const confirmClose = window.confirm(
                'You have unsaved changes. Are you sure you want to close without saving?'
            );
            if (!confirmClose) return;
        }
        onClose();
    };

    const handleContentChange = (event) => {
        setContent(event.target.value);
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="lg" 
            fullWidth
        >
            <DialogTitle>
                Sanitize {docType}
                {hasChanges && <Typography component="span" color="warning.main"> (unsaved changes)</Typography>}
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    {/* Loading State */}
                    {loading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Loading content from Firebase Storage...
                            </Typography>
                        </Box>
                    )}
                    
                    {/* Error State */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {/* Editable Content (Phase 3) */}
                    {!loading && !error && (
                        <TextField
                            label="Document Content"
                            multiline
                            rows={15}
                            fullWidth
                            value={content}
                            onChange={handleContentChange}
                            variant="outlined"
                            sx={{ mb: 2 }}
                            helperText={hasChanges ? "You have unsaved changes" : "Edit the content and click Save to update the file"}
                        />
                    )}
                    
                    {/* Debug Info */}
                    <Typography variant="body2" color="text.secondary">
                        Path: {pathOrUrl || 'Not provided'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Document Type: {docType}
                    </Typography>
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={handleClose}>
                    {hasChanges ? 'Cancel' : 'Close'}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={saveContent}
                    disabled={!hasChanges || saving || loading}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SanitizationModal;